import React, { useState } from "react";
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Skeleton,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

const PERIOD_MAP = {
  week: "This Week",
  month: "This Month",
  authperiod: "Auth Period",
};

const toggleButtonSx = {
  textTransform: "none",
  fontSize: "13px",
  fontWeight: 500,
  lineHeight: "22px",
  letterSpacing: "0.46px",
  px: 2,
  py: 2,
  color: "text.primary",
  "&.Mui-selected": {
    bgcolor: "action.selected",
    color: "text.primary",
    "&:hover": { bgcolor: "action.selected" },
  },
  "&:not(.Mui-selected)": {
    color: "text.primary",
    bgcolor: "transparent",
    "&:hover": { bgcolor: "action.hover" },
  },
};

const SKELETON_ITEMS = 4;

/**
 * Displays client utilization data as donut charts, grouped by billing type.
 * Supports "This Week", "This Month", and "Auth Period" toggle filters.
 *
 * @param props
 * @param props.setBillableTimePeriod - Callback to change the billing period filter
 * @param props.billableTime - Array of billing time data with used/approved units
 * @param props.billableTimeLoading - Whether billing data is currently loading
 * @param props.customerLoaded - Whether the customer data has finished loading (controls skeleton)
 */
const PatientProgress = ({
  setBillableTimePeriod,
  billableTime = [],
  billableTimeLoading,
  customerLoaded = true,
}) => {
  const [currentPeriod, setCurrentPeriod] = useState("week");

  const handlePeriodChange = (_event, newPeriod) => {
    if (newPeriod !== null) {
      setCurrentPeriod(newPeriod);
      setBillableTimePeriod(newPeriod);
    }
  };

  if (!customerLoaded) {
    return (
      <Grid
        container
        component="section"
        aria-label="Loading utilization"
        role="status"
        aria-live="polite"
        data-testid="utilization-loading"
        sx={{
          pt: 5,
          pb: 3,
          px: 3,
          gap: 5,
          borderRadius: "16px",
          bgcolor: "grey.50",
          my: 7,
          maxWidth: "1200px",
          mx: "auto",
        }}
      >
        <Grid
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 0,
          }}
        >
          <Skeleton variant="text" width="15%" height={40} />
          <Skeleton
            variant="rounded"
            width="35%"
            height={36}
            sx={{ borderRadius: "12px" }}
          />
        </Grid>
        <Grid xs={12}>
          <Box
            sx={{
              borderRadius: "16px",
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              py: 10,
              px: 5,
            }}
          >
            {Array.from({ length: SKELETON_ITEMS }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: { xs: "50%", md: "25%" },
                  gap: 3,
                }}
              >
                <Skeleton variant="circular" width={150} height={150} />
                <Skeleton variant="text" width={120} height={24} />
                <Skeleton variant="text" width={100} height={20} />
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      component="section"
      aria-labelledby="utilization-heading"
      sx={{
        pt: 5,
        pb: 3,
        px: 3,
        gap: 5,
        borderRadius: "16px",
        bgcolor: "grey.50",
        my: 7,
        maxWidth: "1200px",
        mx: "auto",
      }}
    >
      {/* Header */}
      <Grid
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 0,
        }}
      >
        <Typography id="utilization-heading" component="h3" variant="h4">
          Utilization
        </Typography>

        <ToggleButtonGroup
          value={currentPeriod}
          exclusive
          size="small"
          onChange={handlePeriodChange}
          aria-label="Utilization period filter"
          sx={{
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "12px",
            p: 1,
            boxShadow: 1,
          }}
        >
          {Object.entries(PERIOD_MAP).map(([value, label]) => (
            <ToggleButton
              key={value}
              value={value}
              aria-label={label}
              sx={toggleButtonSx}
            >
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Grid>

      {/* Content */}
      <Grid xs={12}>
        <Box
          sx={{
            borderRadius: "16px",
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          {billableTimeLoading ? (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-start",
                alignItems: "center",
                py: 10,
                px: 5,
              }}
            >
              {Array.from({ length: SKELETON_ITEMS }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: { xs: "50%", md: "25%" },
                    gap: 3,
                  }}
                >
                  <Skeleton variant="circular" width={150} height={150} />
                  <Skeleton variant="text" width={120} height={24} />
                  <Skeleton variant="text" width={100} height={20} />
                </Box>
              ))}
            </Box>
          ) : billableTime.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 10,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No utilization data available
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-start",
                alignItems: "center",
                py: 10,
                px: 5,
              }}
            >
              {billableTime.map((unit, index) => {
                const usedHours = unit.used_units / 4;
                const approvedHours = unit.approved_units / 4;
                const remainingHours = Math.max(
                  0,
                  (unit.approved_units - unit.used_units) / 4
                );

                return (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      width: { xs: "50%", md: "25%" },
                    }}
                  >
                    <Box sx={{ width: "60%", maxWidth: 180 }}>
                      <CircularProgressbarWithChildren
                        value={unit.used_units}
                        maxValue={unit.approved_units || 1}
                        styles={buildStyles({
                          pathColor: "#40B89F",
                          trailColor: "#C0F3D9",
                          strokeLinecap: "butt",
                        })}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            textAlign: "center",
                            maxWidth: "75%",
                            lineHeight: 1.4,
                          }}
                        >
                          {usedHours} of {approvedHours} hours completed
                        </Typography>
                      </CircularProgressbarWithChildren>
                    </Box>
                    <Box sx={{ mt: 4, textAlign: "center" }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, mb: 1 }}
                      >
                        {unit.cpt_code}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {remainingHours} hours remaining
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default PatientProgress;
